import { setCookie } from 'h3';
import {
  AFFILIATE_REF_COOKIE,
  AFFILIATE_REF_MAX_AGE_SECONDS,
  referralCodeFromUrl,
} from '~/utils/affiliate-ref';

/**
 * Captures the affiliate referral code from a `?ref=<code>` recommendation link
 * into the `mw_affiliate_ref` cookie (7-day TTL, last-click wins). Checkout reads
 * this cookie and forwards it as `affiliate_ref` on order placement. Tracking
 * only — never affects pricing. No-op when the affiliates feature is disabled.
 *
 * The link an affiliate copies is the console's front door, which sits behind
 * the sign-in wall — so for a visitor with no session, the page that renders is
 * never the page the link named. Two things have to hold for that visitor to
 * count, and neither did: an affiliate's new customers signed up through the
 * link and were attributed to nobody.
 *
 * - The SERVER writes the cookie as soon as it reads the request. `useCookie`
 *   only writes when a page renders, and the guard answers `/?ref=…` with a 302
 *   to `/login` that never does, so that response left without it.
 * - The code is also read out of `redirect` (`referralCodeFromUrl`). On the page
 *   the visitor lands on, `/login?redirect=/?ref=…`, `ref` is no longer a
 *   parameter of its own.
 */
export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig();
  if (!config.public.affiliatesEnabled) return;

  const options = {
    maxAge: AFFILIATE_REF_MAX_AGE_SECONDS,
    sameSite: 'lax',
    path: '/',
  } as const;

  if (import.meta.server) {
    // The request's own URL, not the route: the router has not resolved it yet,
    // and a guard that redirects this request resolves it into another page.
    const event = useRequestEvent();
    const code = referralCodeFromUrl(useRequestURL().href);
    if (event && code) setCookie(event, AFFILIATE_REF_COOKIE, code, options);
    return;
  }

  // In the browser: the page that loaded, then every client-side navigation —
  // signing in hands the visitor back to `/?ref=…` without asking the server.
  const route = useRoute();
  const cookie = useCookie<string | null>(AFFILIATE_REF_COOKIE, {
    ...options,
    default: () => null,
  });
  const capture = (url: string) => {
    const code = referralCodeFromUrl(url);
    if (code) cookie.value = code;
  };

  capture(window.location.href);
  watch(() => route.fullPath, capture);
});
